/*
  Phatstix CLIPPY — email capture at 25% scroll depth OR 10s on the page, whichever first (30 Sep 2026; was 50% scroll only).

  Engine is pithings/clippy 0.1.0 (MIT, https://github.com/pithings/clippy), vendored unmodified
  as assets/ph-clippy-engine.js. The character data and sprite are a REDUCED build, 19 of the 43
  animations, produced by _Tools/ph-clippy-repack.py. This file is the Phatstix side: when he
  shows up, the yellow bubble, what a click on him does, and the sign-up.

  No sound, no Web Speech, no AI. The library's own speech balloon is never used: our bubble is
  real markup with a real <form> in it, so it can be styled, translated and typed into.

  Suppression, in order:
    - the feature is off in Theme settings          -> the snippet does not render at all
    - a logged-in customer                          -> data-customer="1"
    - a page we never interrupt (cart)              -> data-suppress="1"
    - this browser already gave us an address       -> ph_email_captured, forever
    - they arrived from a Klaviyo email (_kx=)      -> mark captured, never ask
    - Klaviyo's onsite script knows them            -> klaviyo.isIdentified(), fails open
    - they dismissed it                             -> ph_clippy_snooze, 30 days

  THE KLAVIYO POST is the same one assets/ph-waitlist.js makes: same endpoint, same revision
  header, same body shape. It is duplicated here rather than shared because ph-waitlist.js was
  being edited elsewhere when this was written. Extracting both into one assets/ph-klaviyo.js is
  a named follow-up; until then, a change to the endpoint or the revision has to be made TWICE.
  The ph_email_captured key is deliberately generic for that reason: once the waitlist writes it
  too, someone who joined there stops being asked here.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-ph-clippy]');
  if (!root) return;

  var SNOOZE_KEY = 'ph_clippy_snooze';
  var SNOOZE_DAYS = 30;
  // Sooner (Thom, 30 Sep 2026: "activate clippy way sooner"): 25% scroll OR a 10s dwell, whichever first.
  var THRESHOLD = 0.25;
  var DELAY_MS = 10000;
  var NUDGE_MS = 12000;
  // He taps the glass every 15s while the bubble is still sitting there unanswered (Thom, 31 Aug).
  // IdleFingerTap ships SILENT upstream, so sound 10 was attached to it in ph-clippy-agent.js:
  // it is the shortest tick in the pack and the one upstream reuses as a beat inside GetAttention.
  var TAP_MS = 15000;

  // What a click plays. DELIBERATELY NO Idle* ANIMATIONS (Thom, 30 Aug: "90% of the time it's the
  // atomic one"). Two separate reasons, and both had to go:
  //   1. six of the twelve used to be Idle*, which read as ambient fidgeting rather than a reaction;
  //   2. the engine DEFERS any play() while an idle is running - _playInternal() re-queues it behind
  //      this._idlePromise - so picking a non-idle while IdleAtom was mid-loop showed the atom
  //      instead. Clearing the idle first (see the click handler) is the other half of the fix.
  // These nine are all distinct reactions, and the sheet carries every one of them.
  var CLICK_ANIMS = [
    'Wave', 'GetArtsy', 'GetWizardy', 'Alert', 'Explain',
    'GetTechy', 'Thinking', 'Congratulate', 'GetAttention'
  ];
  // A shuffled bag, not a coin toss: every animation plays once before any repeats, so nine clicks
  // give nine different reactions. Random-with-a-last-value-guard still clustered badly.
  var clickBag = [];
  function nextClickAnim(pool) {
    clickBag = clickBag.filter(function (n) { return pool.indexOf(n) !== -1; });
    if (!clickBag.length) {
      clickBag = pool.slice();
      for (var i = clickBag.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var t = clickBag[i]; clickBag[i] = clickBag[j]; clickBag[j] = t;
      }
      // never open a fresh bag on the one that just played
      if (clickBag.length > 1 && clickBag[0] === lastClick) clickBag.push(clickBag.shift());
    }
    lastClick = clickBag.shift();
    return lastClick;
  }

  var company = (root.getAttribute('data-company') || '').trim();
  var listId = (root.getAttribute('data-list') || '').trim();
  if (!company || !listId) return;

  var charEl = root.querySelector('.ph-cl__char');
  var poke = root.querySelector('[data-ph-cl-poke]');
  var still = root.querySelector('[data-ph-cl-still]');
  var win = root.querySelector('.ph-cl__win');
  var form = root.querySelector('[data-ph-cl-form]');
  var input = root.querySelector('#ph-cl-email');
  var errEl = root.querySelector('[data-ph-cl-error]');
  var announcer = document.getElementById('screenreader-announce');

  var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var agent = null;
  var launched = false;
  var closing = false;
  var nudgeTimer = null;
  var tapTimer = null;
  var lastClick = null;

  // ---- storage, every read and write survives private browsing ---------------
  function get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }

  // captured() / markCaptured() come from the shared client on purpose: someone who joined via the
  // waitlist modal must not then be chased by Clippy for the same address, and vice versa.
  function captured() { return window.phKlaviyo.captured(); }
  function markCaptured() { window.phKlaviyo.markCaptured(); }
  function snoozed() { return parseInt(get(SNOOZE_KEY) || '0', 10) > Date.now(); }
  function snooze() { set(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 864e5)); }

  // The POST lives in assets/ph-klaviyo.js, shared with the waitlist modal, so the endpoint, the
  // revision header and the JSON:API body shape exist in exactly one place.
  function subscribe(email) {
    return window.phKlaviyo.subscribe({
      company: company,
      listId: listId,
      email: email,
      source: 'Phatstix site Clippy 10% popup',
      properties: { signup_source: 'clippy_popup' }
    });
  }

  // deliberately loose: Klaviyo validates properly server-side, and an over-strict regex
  // rejects valid addresses. We only catch the obvious nonsense.
  function isValidEmail(email) {
    return !!email && email.indexOf('@') > 0 && email.indexOf('.', email.indexOf('@')) > 0;
  }

  // ---- state -----------------------------------------------------------------
  function show(state) {
    var all = root.querySelectorAll('[data-ph-cl-state]');
    for (var i = 0; i < all.length; i++) {
      all[i].hidden = all[i].getAttribute('data-ph-cl-state') !== state;
    }
  }

  function fail(msg) {
    if (!errEl) return;
    errEl.textContent = msg;
    errEl.hidden = false;
  }

  function eligible() {
    if (root.getAttribute('data-customer') === '1') return false;
    if (root.getAttribute('data-suppress') === '1') return false;
    // arrived from a Klaviyo email: they are already a profile, so never ask
    if (/[?&]_kx=/.test(location.search)) { markCaptured(); return false; }
    if (captured()) return false;
    if (snoozed()) return false;
    return true;
  }

  // Klaviyo's own onsite script is live on phatstix.net via the Klaviyo app extension. Where it
  // is present, klaviyo.isIdentified() is the closest live signal we have to "already on the
  // list". It is a proxy, not proof of membership of this list, so every failure mode below
  // resolves false and the popup still runs: missing script, ad-blocker, throw, or rejection.
  function knownToKlaviyo() {
    var k = window.klaviyo;
    if (!k || typeof k.isIdentified !== 'function') return Promise.resolve(false);
    try {
      return Promise.resolve(k.isIdentified()).then(
        function (v) { return !!v; },
        function () { return false; }
      );
    } catch (e) { return Promise.resolve(false); }
  }

  // ---- the character ---------------------------------------------------------
  // Reduced motion: the engine and the animation data are NEVER fetched. The static rest-pose
  // frame already in the markup stays as it is, so there is no animation loop and no extra 44KB.
  /* DO NOT replace this with a bare dynamic import(). Shopify's asset pipeline minifies and
     TRANSPILES theme JS, and it rewrites `import(url)` into `__toESM(require(url))`. `require` does
     not exist in a browser, so it throws synchronously - before any network request - the catch
     below fires, and you get the bubble with no Clippy and no animations, which is exactly what
     shipped on 31 Aug. The engine assets were never even requested, which is the tell.
     Loading through an injected <script type="module"> keeps the specifier inside a STRING, so
     there is nothing for the bundler to rewrite, and it needs no eval (CSP-safe, unlike
     new Function('u','return import(u)')). */
  function loadModule(url) {
    return new Promise(function (resolve, reject) {
      var key = 'phMod' + Math.random().toString(36).slice(2);
      window[key] = function (m) { try { delete window[key]; } catch (e) {} resolve(m); };
      var s = document.createElement('script');
      s.type = 'module';
      s.textContent = 'import * as m from ' + JSON.stringify(url) + ';window[' + JSON.stringify(key) + '](m);';
      s.onerror = function () { try { delete window[key]; } catch (e) {} reject(new Error('module load failed: ' + url)); };
      document.head.appendChild(s);
      // a module that 404s or throws never calls back, so do not hang the widget forever
      window.setTimeout(function () { if (window[key]) { try { delete window[key]; } catch (e) {} reject(new Error('module timeout: ' + url)); } }, 8000);
    });
  }

  function mount() {
    if (calm) {
      if (poke) { poke.disabled = true; poke.setAttribute('aria-hidden', 'true'); }
      return Promise.resolve();
    }
    var engineUrl = root.getAttribute('data-engine');
    var dataUrl = root.getAttribute('data-agent-data');
    var mapUrl = root.getAttribute('data-map');
    if (!engineUrl || !dataUrl || !mapUrl) return Promise.resolve();

    // Both are ES modules served from assets/, so asset_url gives a same-origin
    // /cdn/shop/t/N/... path and dynamic import() needs no CORS. Nothing here is fetched
    // until the 50% mark, and only for a visitor who is going to see him.
    return Promise.all([loadModule(engineUrl), loadModule(dataUrl)])
      .then(function (res) {
        return res[0].initAgent({
          agent: function () { return Promise.resolve({ default: res[1].default }); },
          // Sounds (Thom, 30 Aug: "also no sounds are playing"). The repack had stripped the per-frame
          // `sound` keys AND we were passing an empty pack, so two things had to change: the keys are
          // back in ph-clippy-agent.js, and the pack is fetched here. It is base64 audio, so it is a
          // separate module fetched only after the trigger fires, never on a normal page view.
          // The engine already no-ops if the browser cannot play mp3, and a failed fetch degrades to
          // silence rather than killing the widget.
          sound: function () {
            return loadModule(root.getAttribute('data-sounds'))
              .then(function (m) { return { default: m.default || {} }; })
              .catch(function () { return { default: {} }; });
          },
          map: function () { return Promise.resolve({ default: mapUrl }); }
        });
      })
      .then(function (a) {
        agent = a;
        // The library appends its sprite div straight to <body> at z-index 10001, above the cart
        // drawer. Re-parent it into the poke button instead; .ph-cl__agent pins it with
        // `position:relative;inset:auto!important`, which also neutralises the library's own
        // resize reposition() without us having to unbind anything.
        agent._el.classList.add('ph-cl__agent');
        if (still && still.parentNode) still.parentNode.replaceChild(agent._el, still);
        else if (charEl) charEl.insertBefore(agent._el, charEl.firstChild);
        // The intro order matters (Thom, 30 Aug): he ARRIVES and WAVES first, and only then does
        // the balloon appear. The library's queue runs these in order and play()'s callback fires
        // when the animation ends, so the bubble is chained off the Greeting rather than timed.
        // The engine's node is in place, so it is safe to reveal the character: the very next
        // thing that paints is Show, the arrival animation, rather than a static frame.
        root.classList.remove('ph-cl--booting');
        agent.show();
        agent.play('Greeting', undefined, revealBubble);
        // Belt and braces: if the sheet is missing Greeting, or the callback never fires, the
        // bubble must still arrive. Whichever happens first wins, revealBubble is idempotent.
        // 5200ms is deliberately ABOVE both the animation and the engine's own guard: Greeting is
        // 39 frames totalling 4450ms (measured from the shipped agent data) and play() force-
        // completes at 5000ms. The first draft used 2600 and the fallback kept winning, so the
        // bubble arrived halfway through the wave, which is the thing this was meant to prevent.
        window.setTimeout(revealBubble, 5200);
      })
      .catch(function () {
        // engine failed: the static frame in the markup is the fallback, so it has to come back
        root.classList.remove('ph-cl--booting');
        revealBubble();
      });
  }

  // The balloon starts out of the way and is revealed when the greeting finishes. Idempotent,
  // because it is called from the animation callback, from a fallback timer, and from the
  // reduced-motion path where there is no animation to wait for.
  var bubbleShown = false;
  function revealBubble() {
    if (bubbleShown || closing) return;
    bubbleShown = true;
    if (win) win.hidden = false;
  }

  // ---- launch ----------------------------------------------------------------
  function launch() {
    if (launched) return;
    launched = true;
    window.removeEventListener('scroll', onScroll);

    show('idle');
    // clear the mobile PDP sticky add-to-cart bar if this template has one on screen
    var bar = document.querySelector('.ph-pdp__sticky');
    if (bar) {
      var h = bar.getBoundingClientRect().height;
      if (h > 0 && getComputedStyle(bar).display !== 'none') {
        root.style.setProperty('--ph-cl-lift', Math.round(h) + 'px');
      }
    }
    // Clippy is on screen immediately; the balloon waits for his greeting to finish.
    if (win) win.hidden = true;
    // Hold the static frame back until the engine's own node is in and Show is about to play,
    // otherwise the rest pose flashes, disappears and the arrival animation starts from nothing.
    if (!calm) root.classList.add('ph-cl--booting');
    root.hidden = false;
    if (announcer) announcer.textContent = root.getAttribute('data-announce') || '';

    mount();
    // Under prefers-reduced-motion there is no engine and no greeting to wait for, so show it now.
    if (calm) revealBubble();

    // one bigger nudge early on, then a quiet tap on the glass every 15s for as long as the
    // bubble goes unanswered. Both stop the moment he is dismissed or the form is submitted.
    if (!calm) {
      nudgeTimer = window.setTimeout(function () {
        if (agent && !closing) agent.play('GetAttention');
      }, NUDGE_MS);
      tapTimer = window.setInterval(function () {
        if (!agent || closing || !bubbleShown) return;
        // same reason as the click handler: a running idle would otherwise defer this
        agent.stop();
        requestAnimationFrame(function () { if (agent && !closing) agent.play('IdleFingerTap'); });
      }, TAP_MS);
    }
  }

  // ---- outro and teardown ----------------------------------------------------
  function teardown() {
    if (closing) return;
    closing = true;
    window.clearTimeout(nudgeTimer);
    window.clearInterval(tapTimer);
    if (win) win.classList.add('ph-cl__win--out');

    var done = false;
    function bin() {
      if (done) return;
      done = true;
      if (agent) { agent.dispose(); agent = null; }
      root.remove();
    }
    if (agent) agent.hide(false, bin);
    // belt and braces: if the Hide callback never fires, bin it anyway
    window.setTimeout(bin, agent ? 1800 : 320);
  }

  function dismiss() {
    if (closing) return;
    snooze();
    teardown();
  }

  function finish() {
    markCaptured();
    if (agent) agent.play('Congratulate');
    // let them read the confirmation, then he plays Hide and leaves for good
    window.setTimeout(teardown, calm ? 2400 : 3800);
  }

  // ---- events ----------------------------------------------------------------
  root.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-ph-cl-close]')) {
      e.preventDefault();
      dismiss();
    }
  });

  // Click Clippy, he does something. Random, never the same one twice running.
  if (poke) {
    poke.addEventListener('click', function () {
      if (!agent || closing) return;
      var pool = [];
      for (var i = 0; i < CLICK_ANIMS.length; i++) {
        if (agent.hasAnimation(CLICK_ANIMS[i])) pool.push(CLICK_ANIMS[i]);
      }
      if (!pool.length) return;
      var pick = nextClickAnim(pool);
      // stop() clears the queue AND exits the running animation, which resolves the engine's
      // _idlePromise. stopCurrent() alone left the idle owning the animator, so the click was
      // deferred behind it. The rAF gives the exit a frame to land before the new one starts.
      agent.stop();
      requestAnimationFrame(function () { if (agent && !closing) agent.play(pick); });
    });
  }

  // Escape closes it, but ONLY while focus is inside the widget. It is not a modal, it does not
  // trap focus, and a global Escape handler would fight the cart drawer's own.
  root.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { e.stopPropagation(); dismiss(); }
  });

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (input && input.value ? input.value : '').trim();
      if (!isValidEmail(email)) {
        fail(root.getAttribute('data-err-email') || 'That does not look like an email address.');
        if (input) input.focus();
        return;
      }
      if (errEl) errEl.hidden = true;
      window.clearTimeout(nudgeTimer);
      show('sending');
      subscribe(email)
        .then(function (res) {
          if (res.status === 202 || res.ok) { show('done'); finish(); }
          else { show('failed'); }
        })
        .catch(function () { show('failed'); });
    });
  }

  var retry = root.querySelector('[data-ph-cl-retry]');
  if (retry) retry.addEventListener('click', function () { show('idle'); if (input) input.focus(); });

  // ---- the trigger: 25% scroll depth, or DELAY_MS on the page -----------------
  var ticking = false;
  function measure() {
    ticking = false;
    var doc = document.documentElement;
    var seen = window.scrollY + window.innerHeight;
    var total = Math.max(doc.scrollHeight, document.body.scrollHeight);
    if (total > 0 && seen / total >= THRESHOLD) launch();
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(measure);
  }

  // Deliberately no check on load: a page too short to scroll never fires, which is the
  // behaviour we want. The listener is passive, so it cannot block scrolling.
  if (eligible()) {
    knownToKlaviyo().then(function (known) {
      if (known) { markCaptured(); return; }
      window.addEventListener('scroll', onScroll, { passive: true });
      // the dwell trigger: launch() is idempotent (the launched flag), so the loser of the race is a no-op
      window.setTimeout(launch, DELAY_MS);
    });
  }
})();
